import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useId,
  useInteractions,
  useRole
} from '@floating-ui/react';
import { type Placement } from '@floating-ui/react';
import { type ReactNode, useRef, useState } from 'react';

import { cn } from '../lib/utils';

const ARROW_HEIGHT = 7;

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  placement?: Placement;
  disabled?: boolean;
  interactive?: boolean;
  showArrow?: boolean;
  className?: string;
  /**
   * Puts the trigger in the tab order, so a reader who does not use a mouse can
   * reach what the tooltip holds. Pass it where the trigger is plain text; an
   * element that already takes focus opens the tooltip on its own.
   */
  focusable?: boolean;
  /** Classes for the element the tooltip anchors to, e.g. to keep a block-level trigger full width. */
  triggerClassName?: string;
}

function Tooltip({
  children,
  content,
  placement = 'top',
  disabled = false,
  focusable = false,
  interactive = false,
  showArrow = true,
  className,
  triggerClassName
}: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [
      offset(ARROW_HEIGHT + 2),
      flip({ fallbackAxisSideDirection: 'start' }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef })
    ],
    whileElementsMounted: autoUpdate
  });

  const hover = useHover(context, {
    move: false,
    enabled: !disabled,
    handleClose: interactive ? safePolygon() : null
  });
  const focus = useFocus(context, { enabled: !disabled });
  const click = useClick(context, { enabled: false });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role
  ]);

  const id = useId();

  if (disabled) return <>{children}</>;

  return (
    <>
      <span
        ref={refs.setReference}
        className={triggerClassName}
        tabIndex={focusable ? 0 : undefined}
        {...getReferenceProps()}
        // Last word on the id, so it names the bubble below rather than the one
        // the role hook generates for itself.
        aria-describedby={isOpen ? id : undefined}>
        {children}
      </span>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            id={id}
            className={cn(
              // The bubble is dark in every theme, so its text must not go
              // through the inversion in dark-mode.css, which rewrites
              // `text-white` to `text-gray-900`. The uniform ramp is untouched
              // by that sheet, so it stays light.
              'z-50 max-w-xs rounded-lg px-3 py-2 text-sm font-medium text-uniform-white-base',
              'bg-uniform-gray-900 shadow-lg dark:bg-uniform-gray-700',
              className
            )}>
            {content}
            {showArrow && (
              <FloatingArrow
                ref={arrowRef}
                context={context}
                height={ARROW_HEIGHT}
                className="fill-uniform-gray-900 dark:fill-uniform-gray-700"
              />
            )}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

export { Tooltip };
export type { TooltipProps };
